import React from 'react';
import { Chip, Typography } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import EmailIcon from '@material-ui/icons/Email';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import TimerOffIcon from '@material-ui/icons/TimerOff';
import Tooltip from '@mui/material/Tooltip';
import { GridColDef } from '@mui/x-data-grid';
import {
  diamGridSingleSelectFilterOperators,
  diamGridStringFilterOperators,
} from '../../diamGridUtils';
import {
  ActionsContainer,
  ActionButton,
  ChipContainer,
  EmailText,
  OverflowChip,
  OverflowIndicator,
  PrimaryChip,
  StatusContainer,
  getInvitationStatusColor,
} from './styles';
import {
  Invitation,
  InvitationActionHandlers,
  InvitationStatus,
} from './types';
import { parseExpiresAt } from './expiresDateFilter';
import { formatExpiresAtDisplay } from './expiresDateUtils';

const STATUS_OPTIONS: Array<{ value: InvitationStatus; label: string }> = [
  { value: 'pending', label: 'Pending' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'expired', label: 'Expired' },
  { value: 'revoked', label: 'Revoked' },
];

const STATUS_SORT_ORDER: Record<InvitationStatus, number> = {
  pending: 0,
  expired: 1,
  revoked: 2,
  accepted: 3,
  rejected: 4,
};

function StatusIcon({
  status,
  color,
}: {
  status: InvitationStatus;
  color: string;
}) {
  switch (status) {
    case 'pending':
      return <EmailIcon fontSize="small" style={{ color }} />;
    case 'accepted':
      return <CheckCircleIcon fontSize="small" style={{ color }} />;
    case 'expired':
      return <TimerOffIcon fontSize="small" style={{ color }} />;
    case 'rejected':
      return <RemoveCircleOutlineIcon fontSize="small" style={{ color }} />;
    case 'revoked':
      return <HighlightOffIcon fontSize="small" style={{ color }} />;
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

function InvitationStatusCell({ status }: { status: InvitationStatus }) {
  const theme = useTheme();
  const color = getInvitationStatusColor(status, theme.palette);

  return (
    <StatusContainer>
      <StatusIcon status={status} color={color} />
      <Typography
        variant="body2"
        style={{
          color,
          fontWeight: 600,
          textTransform: 'capitalize',
        }}
      >
        {status}
      </Typography>
    </StatusContainer>
  );
}

function renderChipList(items: string[]) {
  if (items.length === 0) {
    return <ChipContainer />;
  }

  const hiddenCount = items.length - 1;

  return (
    <ChipContainer>
      <PrimaryChip color="primary" label={items[0]} />
      {hiddenCount > 0 && (
        <>
          <OverflowIndicator>...</OverflowIndicator>
          <OverflowChip color="primary" label={`+${hiddenCount}`} />
        </>
      )}
    </ChipContainer>
  );
}

export function buildInvitationColumns({
  onInviteManually,
  onResend,
  onRevoke,
}: InvitationActionHandlers): GridColDef<Invitation>[] {
  return [
    {
      field: 'email',
      headerName: 'Email',
      flex: 1.1,
      minWidth: 200,
      filterOperators: diamGridStringFilterOperators,
      renderCell: ({ row }) => (
        <EmailText variant="body2">{row.email}</EmailText>
      ),
    },
    {
      field: 'teamName',
      headerName: 'Team',
      flex: 1,
      minWidth: 180,
      filterable: false,
      sortable: false,
      renderCell: ({ row }) => (
        <Tooltip title={row.teamName}>
          <Chip color="primary" label={row.teamName} />
        </Tooltip>
      ),
    },
    {
      field: 'roles',
      headerName: 'Roles',
      flex: 1.2,
      minWidth: 220,
      filterable: false,
      sortable: false,
      valueGetter: (_value, row) => row.roles.join(', '),
      renderCell: ({ row }) => (
        <Tooltip title={row.roles.join(', ')}>
          {renderChipList(row.roles)}
        </Tooltip>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.7,
      minWidth: 120,
      type: 'singleSelect',
      valueOptions: STATUS_OPTIONS,
      filterOperators: diamGridSingleSelectFilterOperators,
      sortComparator: (value1, value2, cellParams1, cellParams2) => {
        const statusDiff =
          STATUS_SORT_ORDER[value1 as InvitationStatus] -
          STATUS_SORT_ORDER[value2 as InvitationStatus];

        if (statusDiff !== 0) {
          return statusDiff;
        }

        const row1 = cellParams1.api.getRow(cellParams1.id) as Invitation;
        const row2 = cellParams2.api.getRow(cellParams2.id) as Invitation;
        const date1 = parseExpiresAt(row1.expiresAt)?.getTime() ?? 0;
        const date2 = parseExpiresAt(row2.expiresAt)?.getTime() ?? 0;

        return date1 - date2;
      },
      renderCell: ({ row }) => <InvitationStatusCell status={row.status} />,
    },
    {
      field: 'expiresAt',
      headerName: 'Expires',
      flex: 0.7,
      minWidth: 110,
      type: 'date',
      filterable: false,
      sortable: false,
      getApplyQuickFilterFn: () => null,
      valueGetter: (_value, row) => parseExpiresAt(row.expiresAt),
      renderCell: ({ row }) => formatExpiresAtDisplay(row.expiresAt),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      align: 'left',
      cellClassName: 'actions-cell',
      filterable: false,
      getApplyQuickFilterFn: () => null,
      headerAlign: 'left',
      minWidth: 250,
      sortable: false,
      renderCell: ({ row }) => {
        const showInviteManually = row.status === 'pending';
        const showRevoke = row.status === 'pending';
        const showResend =
          row.status === 'expired' || row.status === 'rejected';

        if (!showInviteManually && !showResend && !showRevoke) {
          return null;
        }

        return (
          <ActionsContainer>
            {showInviteManually && (
              <ActionButton
                variant="contained"
                color="primary"
                size="small"
                onClick={event => {
                  event.stopPropagation();
                  onInviteManually?.(row);
                }}
              >
                Invite Manually
              </ActionButton>
            )}
            {showRevoke && (
              <ActionButton
                variant="contained"
                color="primary"
                size="small"
                onClick={event => {
                  event.stopPropagation();
                  onRevoke?.(row);
                }}
              >
                Revoke
              </ActionButton>
            )}
            {showResend && (
              <ActionButton
                variant="contained"
                color="primary"
                size="small"
                onClick={event => {
                  event.stopPropagation();
                  onResend?.(row);
                }}
              >
                Resend
              </ActionButton>
            )}
          </ActionsContainer>
        );
      },
    },
  ];
}
